import { authFetch } from "@/lib/authFetch";
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Users,
  Clock,
  TrendingUp,
  Menu,
  Target,
  Layers,
  RotateCcw,
  Award,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import Sidebar from "@/components/Sidebar";
import { DonutChartCard, TableCardHalf } from "@/components/DashboardCharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status Badge
const StatusBadge = ({ status }) => {
  const statusStyles = {
    "Referral Submitted": "bg-blue-100 text-blue-800 border-blue-200",
    "Intake Complete": "bg-indigo-100 text-indigo-800 border-indigo-200",
    "Credit Active": "bg-amber-100 text-amber-800 border-amber-200",
    "In Progress": "bg-purple-100 text-purple-800 border-purple-200",
    "Ready for Lender": "bg-emerald-100 text-emerald-800 border-emerald-200",
    "Loan Ready": "bg-green-100 text-green-800 border-green-200",
    default: "bg-slate-100 text-slate-800 border-slate-200",
  };

  return (
    <Badge variant="outline" className={statusStyles[status] || statusStyles.default}>
      {status || "Unknown"}
    </Badge>
  );
};

const TeamDashboard = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await authFetch(`${API}/stats/dashboard`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewReportClients = (reportType, value) => {
    const basePath = `/team/clients`;
    const params = new URLSearchParams();
    if (reportType === "loan_ready_stage") params.append("loan_ready_stage", value);
    else if (reportType === "lead_status") params.append("lead_status", value);
    else if (reportType === "rounds") params.append("rounds", value);
    else if (reportType === "client_progress") params.append("client_progress", value);
    else if (reportType === "status") params.append("status", value);
    navigate(`${basePath}?${params.toString()}`);
  };

  return (
    <div className={`flex min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-black' : 'bg-slate-50'}`}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isAdmin={false} />

      <main className="flex-1 min-w-0 md:ml-64">
        <header className="header-glass sticky top-0 z-30 px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-slate-900">My Clients</h1>
                <p className="text-sm text-slate-500">Welcome back, {user?.name?.split(" ")[0]}</p>
              </div>
            </div>
          </div>
        </header>

        <div className="p-4 md:p-6 space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card data-testid="stat-my-clients">
              <CardHeader className="pb-2">
                <CardDescription>My Clients</CardDescription>
                <CardTitle className="text-3xl">
                  {stats?.total_clients ?? <Skeleton className="h-9 w-16" />}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500">Assigned to you</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-in-progress">
              <CardHeader className="pb-2">
                <CardDescription>In Progress</CardDescription>
                <CardTitle className="text-3xl text-amber-600">
                  {stats?.in_progress ?? 0}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500">In Loan Ready program</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-loan-ready">
              <CardHeader className="pb-2">
                <CardDescription>Loan Ready</CardDescription>
                <CardTitle className="text-3xl text-emerald-600">
                  {stats?.loan_ready ?? 0}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500">Completed / Graduated</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-new-referrals">
              <CardHeader className="pb-2">
                <CardDescription>New Referrals</CardDescription>
                <CardTitle className="text-3xl text-blue-600">
                  {stats?.new_referrals ?? 0}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500">Awaiting intake</p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="card-hover" data-testid="upcoming-deadlines-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-amber-500" />
                  Upcoming Deadlines
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.upcoming_deadlines?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.upcoming_deadlines.slice(0, 3).map((client) => (
                      <Link
                        key={client.client_id}
                        to={`/team/clients/${client.client_id}`}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        <span className="font-medium text-sm">
                          {client.first_name} {client.last_name}
                        </span>
                        <span className="text-sm text-amber-600">
                          {client.betenbough_deadline_date_for_loan_ready}
                        </span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No upcoming deadlines</p>
                )}
              </CardContent>
            </Card>

            <Card className="card-hover" data-testid="recent-updates-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-teal-500" />
                  Recent Updates
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.recent_updates?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_updates.slice(0, 3).map((client) => (
                      <Link
                        key={client.client_id}
                        to={`/team/clients/${client.client_id}`}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        <span className="font-medium text-sm">
                          {client.first_name} {client.last_name}
                        </span>
                        <StatusBadge status={client.betenbough_client_status} />
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No recent updates</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Charts Section */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <DonutChartCard
                title="Lead Status & Stages"
                icon={Layers}
                data={stats.lead_status_stages}
                reportType="lead_status"
                onViewClients={handleViewReportClients}
                isDarkMode={isDarkMode}
              />
              <DonutChartCard
                title="Loan Ready Stages"
                icon={Target}
                data={stats.loan_ready_stages}
                reportType="loan_ready_stage"
                onViewClients={handleViewReportClients}
                isDarkMode={isDarkMode}
              />
              <TableCardHalf
                title="Credit Rounds"
                icon={RotateCcw}
                data={stats.credit_rounds}
                reportType="rounds"
                onViewClients={handleViewReportClients}
                isDarkMode={isDarkMode}
              />
              <TableCardHalf
                title="Loan Ready Grats Stage"
                icon={Award}
                data={stats.loan_ready_grats}
                reportType="client_progress"
                onViewClients={handleViewReportClients}
                isDarkMode={isDarkMode}
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TeamDashboard;
